import React from "react"
import PropTypes from "prop-types"

import moment from "moment"
import { post } from "shared/lib/api"

import proBadge from "images/plan-badges/pro.svg"
import proPlusBadge from "images/plan-badges/pro_plus.svg"

const Activity = ({
  connection,
  activityURL,
  checkConnectionURL,
  userTier,
}) => {
  const {
    id,
    config_type,
    status,
    created_at,
    last_run,
    run_count,
    speed,
  } = connection

  // queries
  function getCheckConnectionAction() {
    const paused = connection.channels.filter(c => c.is_paused)

    // disable the action if any services are paused
    if (paused.length !== 0) {
      return {
        label: getPausedLabel(paused),
        call: null,
      }
    }

    // otherwse, this can be checked
    return {
      label: speed,
      call() {
        post(checkConnectionURL)
          .then(() => window.appendFlash("Check completed"))
          .catch(() =>
            window.appendFlash(
              "There was a problem checking this Applet.",
              "danger"
            )
          )
      },
    }
  }

  function getPausedLabel(services) {
    const names = services.map(c => c.name).join(" & ")
    const pluralized = services.length > 1 ? "services are" : "service is"
    return `${names} ${pluralized} <a class="with-decoration" target="_blank" href="https://ift.tt/paused-services" title="Learn more about paused services">currently paused</a>.`
  }

  // render
  const isStaticApplet = config_type === "static"
  const checkConnectionAction = getCheckConnectionAction()

  return (
    status === "enabled_for_user" &&
    (created_at || last_run || run_count) && (
      <>
        <hr />
        <div className="connection-view-activity">
          <div className="connection-list-activity">
            <ul>
              {created_at && (
                <li>
                  Connected{" "}
                  {moment(created_at, "YYYY-MM-DD HH:mm:ss ZZZZ").format(
                    "MMM DD, YYYY"
                  )}
                </li>
              )}

              {isStaticApplet && (
                <>
                  {last_run && (
                    <li>
                      Last activity{" "}
                      {moment(last_run, "YYYY-MM-DD HH:mm:ss ZZZZ").format(
                        "MMM DD, YYYY"
                      )}
                    </li>
                  )}

                  {run_count ? (
                    <li>
                      Run {run_count} time{run_count > 1 && <span>s</span>}
                    </li>
                  ) : (
                    <li>Never run</li>
                  )}
                </>
              )}
            </ul>

            <a
              role="button"
              className="button-tertiary button-outlined"
              href={activityURL}
              data-track-ifttt-next-event="click"
              data-track-data={JSON.stringify({
                object_type: "connection_activity_log",
                object_id: `${id}`,
                location_type: "connection_config",
              })}
            >
              View activity
            </a>
          </div>

          {isStaticApplet && (
            <div className="connection-check">
              {userTier === "pro" && (
                <span
                  className="badge pro_plus"
                  dangerouslySetInnerHTML={{ __html: proPlusBadge }}
                />
              )}
              {["intermediate_pro", "legacy_pro"].includes(userTier) && (
                <span
                  className={`badge ${userTier}`}
                  dangerouslySetInnerHTML={{ __html: proBadge }}
                />
              )}
              <span
                className="advise"
                dangerouslySetInnerHTML={{
                  __html: checkConnectionAction.label,
                }}
              />
              <button
                className="button-tertiary button-outlined"
                disabled={checkConnectionAction.call == null}
                title="IFTTT will check the trigger service and run the actions if new events are returned"
                onClick={checkConnectionAction.call}
                data-track-ifttt-next-event="click"
                data-track-data={JSON.stringify({
                  object_type: "connection_activity_check_now",
                  object_id: `${id}`,
                  location_type: "connection_config",
                })}
              >
                Check now
              </button>
            </div>
          )}
        </div>
      </>
    )
  )
}

Activity.propTypes = {
  connection: PropTypes.shape({
    id: PropTypes.string,
    config_type: PropTypes.string,
    created_at: PropTypes.string,
    last_run: PropTypes.string,
    run_count: PropTypes.number,
    status: PropTypes.string,
    speed: PropTypes.string,
    channels: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        is_paused: PropTypes.bool,
      })
    ),
  }),
  activityURL: PropTypes.string,
  checkConnectionURL: PropTypes.string,
  userTier: PropTypes.string,
}

export default Activity
